/**
 * Native ES module produced from the historical shared-scope controller.
 * Cross-domain dependencies are explicit imports; eager DOM wiring is isolated in the init function.
 */
import { openMyEventRegistrations } from '../community/events-notes-navigator.runtime.js';
import { openAutopilotSettings, simpleHubCard } from '../courses/courses.runtime.js';
import { attendanceClass, attendanceLabel } from '../knowledge/materials-attendance-wiki.runtime.js';
import { applyAccentColor, isRussianInterfaceStyle, normalizeInterfacePreferences, normalizeNavigationOrder } from '../onboarding/onboarding.runtime.js';
import { mountSimpleViewLinks, uiIcon } from '../platform/android-uikit.runtime.js';
import { $, $$, SIMPLE_MORE_VIEWS, SIMPLE_STUDY_VIEWS, api, availableSectionViews, compactMode, contextMode, escapeHTML, fmtDate, fmtDay, fmtTime, isAdmin, sectionForView, simpleParentView, state, superGrokMode, t, toast, trackProduct } from '../platform/core.runtime.js';
import { closeDialog, openDialog } from '../platform/dialog-boot.runtime.js';
import { openScheduleDialog, openSeriesManager, openTeacherUnavailableDialog, showSchedule } from '../schedule/lesson.runtime.js';
import { canManageGroup, currentGroupLabel, openGroupSwitcher, setupMFAOptional } from '../session/session.runtime.js';
import { headAction, isToday, isoWeekNumber, mondayFor, openTelegramBot, renderView, requireGroup, resetHeadActions, saveAppearancePreferences, setView } from '../shell/navigation.runtime.js';
import { aiGroupAwarePath } from '../study/homework-ai.runtime.js';

export async function renderSettings() {
  resetHeadActions();
  const root = $('#content');
  const prefs = normalizeInterfacePreferences(state.interfacePreferences);
  const mfaSecurity = await api('/auth/mfa/status').catch(()=>({privileged:false,enabled:false,session_verified:false}));
  const legalStatus = await api('/legal/status').catch(()=>(state.legalStatus||{}));
  const aiKeyStatus = await api(aiGroupAwarePath('/ai/keys/mistral')).catch(()=>({server_configured:false,byok_configured:false,byok_enabled:false,effective_source:'',access_enabled:true,server_allowed:true,byok_allowed:true}));
  state.legalStatus = legalStatus;
  const presets = [
    ['#1668E3','Синий','blue'],['#7C3AED','Фиолетовый','violet'],['#D946EF','Розовый','magenta'],
    ['#E0562B','Оранжевый','orange'],['#168A67','Зелёный','green'],['#C33C54','Красный','red'],
  ];
  root.innerHTML = `<div class="settings-page${contextMode()||superGrokMode()?' x10-page sg-page':''}">
    <header class="settings-page-head">
      <div><span class="section-label">Аккаунт</span><h2>Настройки</h2><p>Оформление хранится на сервере и применяется после входа на любом устройстве — в браузере, PWA и Android-приложении.</p></div>
    </header>

    <section class="settings-section">
      <header><strong>Темы</strong><span>Светлая, тёмная или системная</span></header>
      <div class="settings-surface settings-appearance-surface">
        <div class="settings-item settings-theme-item">
          <div class="settings-copy"><strong>Оформление</strong><small>Тёмная используется по умолчанию для новых аккаунтов</small></div>
          <div class="theme-choice" role="group" aria-label="Тема оформления">
            <button type="button" data-theme-choice="dark" class="${prefs.theme==='dark'?'active':''}" aria-pressed="${prefs.theme==='dark'}"><span class="theme-dot dark"></span>Тёмная</button>
            <button type="button" data-theme-choice="light" class="${prefs.theme==='light'?'active':''}" aria-pressed="${prefs.theme==='light'}"><span class="theme-dot light"></span>Светлая</button>
            <button type="button" data-theme-choice="system" class="${prefs.theme==='system'?'active':''}" aria-pressed="${prefs.theme==='system'}"><span class="theme-dot system"></span>Системная</button>
          </div>
        </div>
      </div>
    </section>

    <section class="settings-section">
      <header><strong>Интерфейсы</strong><span>Структура и навигация</span></header>
      <div class="settings-mode-list" role="radiogroup" aria-label="Тип интерфейса">
        ${[
          ['supergrok','Фокус','Только важное, быстрые действия, минимум шума'],
          ['simple','Простой','Главная, расписание, учёба и ещё'],
          ['context','Контекст','Страницы вокруг пары, меньше карточек'],
          ['classic','Обычный','Полная навигация по всем разделам'],
        ].map(([value,title,description])=>`<button class="settings-mode-option settings-mode-option-${value} ${prefs.interface_mode===value?'active':''}" type="button" data-settings-mode="${value}" role="radio" aria-checked="${prefs.interface_mode===value}"><span class="settings-mode-radio"></span><span><strong>${title}</strong><small>${description}</small></span><span class="settings-mode-check">✓</span></button>`).join('')}
      </div>
    </section>

    <section class="settings-section">
      <header><strong>Стили</strong><span>Визуальный язык поверх любого интерфейса</span></header>
      <div class="settings-mode-list settings-style-list" role="radiogroup" aria-label="Стиль интерфейса">
        ${[
          ['default','kUni','Базовый спокойный стиль приложения'],
          ['bento','Bento Campus','Тёплые модульные плитки, коралл, шалфей, охра и слива'],
          ['night','Night Shift','Техническая рабочая станция: тонкие линии, циан, коралл и моноширинные данные'],
          ['kawaii','Kawaii Kitty','Молочно-розовый kawaii: бантики, сердечки, мягкие карточки и котик-маскот'],
          ['tor','Tor Layers','Фиолетовая ночь · слои как circuit Tor · без маскота'],
          ['khokhloma','Хохлома','Чёрный лак, киноварь и золотая травка — как расписная ложка'],
          ['rzd','РЖД / Табло','Вокзальное табло: янтарь, сталь и красная полоса'],
          ['gzhel','Гжель','Кобальт по фарфору: завитки, белила, холодный блеск']
        ].map(([value,title,description])=>`<button class="settings-mode-option settings-style-option settings-style-option-${value} ${prefs.interface_style===value?'active':''}" type="button" data-settings-style="${value}" role="radio" aria-checked="${prefs.interface_style===value}"><span class="settings-mode-radio"></span><span><strong>${title}</strong><small>${description}</small></span><span class="settings-mode-check">✓</span></button>`).join('')}
        <p class="settings-mode-note">Стиль не меняет структуру приложения и не переключает тему. Работает с любой темой и любым интерфейсом.</p>
      </div>
      <div class="settings-surface settings-chronicle-surface" id="settings-chronicle-surface" ${isRussianInterfaceStyle(prefs.interface_style)?'':'hidden'}>
        <label class="settings-item settings-chronicle-item" for="settings-chronicle-initials">
          <span class="settings-chronicle-sample letopis-skip" aria-hidden="true"><b>В</b>ъ лето</span>
          <div class="settings-copy">
            <strong>Буквица</strong>
            <small>Первая буква предложения — большая и киноварная, как в летописи</small>
          </div>
          <input id="settings-chronicle-initials" type="checkbox" ${prefs.chronicle_initials?'checked':''}>
        </label>
      </div>
      <div class="settings-surface settings-style-accent-surface">
        <div class="settings-item settings-accent-item">
          <label class="settings-copy" for="settings-accent-color"><strong>Акцент базового kUni</strong><small id="settings-accent-value">${escapeHTML(prefs.accent_color)}${prefs.interface_style!=='default'?' · сохранён; выбранный стиль использует собственную палитру':''}</small></label>
          <div class="settings-accent-controls">
            <input id="settings-accent-color" type="color" value="${escapeHTML(prefs.accent_color)}" aria-label="Акцентный цвет базового стиля">
            <div class="settings-accent-presets" aria-label="Готовые акцентные цвета">${presets.map(([color,name,key])=>`<button class="settings-accent-preset settings-accent-preset-${key}" type="button" data-settings-accent="${color}" title="${name}" aria-label="${name}"></button>`).join('')}</div>
          </div>
        </div>
      </div>
    </section>

    <section class="settings-section">
      <header><strong>Безопасность</strong><span>Доступ к аккаунту</span></header>
      <div class="settings-surface">
        <button class="settings-link" type="button" data-settings-mfa><span class="settings-link-icon">2</span><span class="settings-copy"><strong>Двухфакторная аутентификация</strong><small>${mfaSecurity.privileged?'Обязательна для вашей роли':mfaSecurity.enabled?'Защищает вход дополнительным TOTP-кодом':'Можно включить по желанию'}</small></span><span class="security-status ${mfaSecurity.enabled?'good':'warn'}">${mfaSecurity.enabled?'Включена':'Не включена'}</span></button>
      </div>
    </section>

    <section class="settings-section">
      <header><strong>kUni AI</strong><span>Ключ и источник оплаты запросов</span></header>
      <div class="settings-surface">
        <button class="settings-link" type="button" data-settings-ai-key><span class="settings-link-icon">✦</span><span class="settings-copy"><strong>Bring your own Mistral key</strong><small>${!aiKeyStatus.access_enabled?'kUni AI отключён политикой администратора':aiKeyStatus.byok_configured&&aiKeyStatus.byok_allowed===false?`Личный ключ сохранён, но BYOK запрещён политикой · ${escapeHTML(aiKeyStatus.byok_hint||'')}`:aiKeyStatus.byok_configured?`${aiKeyStatus.effective_source==='byok'?'Используется личный ключ':aiKeyStatus.byok_enabled?'Личный ключ включён, но сейчас используется другой источник':'Личный ключ сохранён, но выключен'} · ${escapeHTML(aiKeyStatus.byok_hint||'')}`:aiKeyStatus.server_configured&&aiKeyStatus.server_allowed!==false?`Сейчас используется серверный ключ · ${escapeHTML(aiKeyStatus.server_hint||'')}`:aiKeyStatus.server_configured?'Серверный ключ настроен, но запрещён политикой':'Серверный ключ не настроен — можно добавить свой, если BYOK разрешён'}</small></span><span class="security-status ${aiKeyStatus.effective_source?'good':'warn'}">${!aiKeyStatus.access_enabled?'Off':aiKeyStatus.effective_source==='byok'?'BYOK':aiKeyStatus.effective_source?'Server':'Нет ключа'}</span></button>
        <button class="settings-link" type="button" data-settings-autopilot><span class="settings-link-icon">↻</span><span class="settings-copy"><strong>kUni Autopilot</strong><small>После пары дождётся ДЗ, решит по выбранным источникам и при желании пришлёт PDF</small></span>${uiIcon('chevron',17)}</button>
      </div>
    </section>

    <section class="settings-section">
      <header><strong>${escapeHTML(t('legal.settings.title'))}</strong><span>${escapeHTML(t('legal.settings.subtitle'))}</span></header>
      <div class="settings-surface">
        <button class="settings-link" type="button" data-settings-privacy><span class="settings-link-icon">§</span><span class="settings-copy"><strong>${escapeHTML(t('legal.links.privacy'))}</strong><small>${escapeHTML(legalStatus?.privacy_version || '')}</small></span>${uiIcon('chevron',17)}</button>
        <button class="settings-link" type="button" data-settings-consent><span class="settings-link-icon">✓</span><span class="settings-copy"><strong>${escapeHTML(t('legal.links.consent'))}</strong><small>${legalStatus?.accepted?escapeHTML(t('legal.settings.accepted')):escapeHTML(t('legal.gate.title'))} · ${escapeHTML(legalStatus?.consent_version || '')}</small></span>${uiIcon('chevron',17)}</button>
      </div>
    </section>

    <section class="settings-section settings-account-section">
      <header><strong>Аккаунт</strong><span>${escapeHTML(state.user?.email || '')}</span></header>
      <div class="settings-surface">
        <button class="settings-link" type="button" data-settings-group><span class="settings-link-icon">Г</span><span class="settings-copy"><strong>Группа</strong><small data-current-group-label>${escapeHTML(currentGroupLabel())}</small></span>${uiIcon('chevron',17)}</button>
        <button class="settings-link" type="button" data-settings-registrations><span class="settings-link-icon">★</span><span class="settings-copy"><strong>Мои регистрации</strong><small>Мероприятия, на которые ты записан</small></span>${uiIcon('chevron',17)}</button>
        <button class="settings-link" type="button" data-settings-notifications><span class="settings-link-icon">●</span><span class="settings-copy"><strong>Уведомления</strong><small>Расписание, дедлайны и посещаемость</small></span>${uiIcon('chevron',17)}</button>
        <button class="settings-link" type="button" data-settings-telegram><span class="settings-link-icon settings-telegram-icon">➤</span><span class="settings-copy"><strong>Telegram-бот</strong><small>Привязка аккаунта и быстрые действия</small></span>${uiIcon('chevron',17)}</button>
        <button class="settings-link danger-link" type="button" data-settings-logout><span class="settings-link-icon">↪</span><span class="settings-copy"><strong>Выйти</strong><small>Завершить текущий сеанс</small></span></button>
      </div>
    </section>
  </div>`;

  const sync = () => {
    const current = normalizeInterfacePreferences(state.interfacePreferences);
    $$('[data-theme-choice]', root).forEach((button) => {
      const active = button.dataset.themeChoice === current.theme;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    $$('[data-settings-mode]', root).forEach((button) => {
      const active = button.dataset.settingsMode === current.interface_mode;
      button.classList.toggle('active', active);
      button.setAttribute('aria-checked', String(active));
    });
    $$('[data-settings-style]', root).forEach((button) => {
      const active = button.dataset.settingsStyle === current.interface_style;
      button.classList.toggle('active', active);
      button.setAttribute('aria-checked', String(active));
    });
    const color = $('#settings-accent-color', root);
    if (color) color.value = current.accent_color;
    const label = $('#settings-accent-value', root);
    if (label) label.textContent = `${current.accent_color}${current.interface_style!=='default'?' · сохранён; выбранный стиль использует собственную палитру':''}`;
    const chronicle = $('#settings-chronicle-surface', root);
    if (chronicle) chronicle.hidden = !isRussianInterfaceStyle(current.interface_style);
    const chronicleBox = $('#settings-chronicle-initials', root);
    if (chronicleBox) chronicleBox.checked = Boolean(current.chronicle_initials);
  };

  $$('[data-theme-choice]', root).forEach((button) => button.addEventListener('click', async () => {
    if (button.dataset.themeChoice === state.interfacePreferences.theme) return;
    button.disabled = true;
    try { await saveAppearancePreferences({ theme: button.dataset.themeChoice }, 'Тема сохранена'); }
    catch { /* rollback already applied */ }
    finally { button.disabled = false; sync(); }
  }));

  const colorInput = $('#settings-accent-color', root);
  colorInput?.addEventListener('input', (event) => {
    applyAccentColor(event.target.value);
    const label = $('#settings-accent-value', root); if (label) label.textContent = String(event.target.value).toUpperCase();
  });
  colorInput?.addEventListener('change', async (event) => {
    try { await saveAppearancePreferences({ accent_color: event.target.value }, 'Акцент сохранён'); }
    catch { /* rollback already applied */ }
    finally { sync(); }
  });
  $$('[data-settings-accent]', root).forEach((button) => button.addEventListener('click', async () => {
    try { await saveAppearancePreferences({ accent_color: button.dataset.settingsAccent }, 'Акцент сохранён'); }
    catch { /* rollback already applied */ }
    finally { sync(); }
  }));

  $$('[data-settings-mode]', root).forEach((button) => button.addEventListener('click', async () => {
    const mode = button.dataset.settingsMode;
    if (mode === state.interfacePreferences.interface_mode) return;
    button.disabled = true;
    try { await saveAppearancePreferences({ interface_mode: mode }, 'Интерфейс сохранён'); }
    catch { /* rollback already applied */ }
    finally { button.disabled = false; sync(); }
  }));

  $$('[data-settings-style]', root).forEach((button) => button.addEventListener('click', async () => {
    const style = button.dataset.settingsStyle;
    if (style === state.interfacePreferences.interface_style) return;
    button.disabled = true;
    try { await saveAppearancePreferences({ interface_style: style }, 'Стиль сохранён'); }
    catch { /* rollback already applied */ }
    finally { button.disabled = false; sync(); }
  }));

  $('#settings-chronicle-initials', root)?.addEventListener('change', async (event) => {
    const on = Boolean(event.target.checked);
    event.target.disabled = true;
    try { await saveAppearancePreferences({ chronicle_initials: on }, on ? 'Буквица включена' : 'Буквица выключена'); }
    catch { /* rollback already applied */ }
    finally { event.target.disabled = false; sync(); }
  });

  $('[data-settings-mfa]', root)?.addEventListener('click', async () => {
    if(!mfaSecurity.enabled){
      try{if(await setupMFAOptional()){toast('Двухфакторная аутентификация включена');await renderSettings();}}catch(error){toast(error.message);}
      return;
    }
    if(mfaSecurity.privileged){toast('Для вашей роли 2FA обязательна. Сброс доступен администратору.');return;}
    openDialog('Отключить двухфакторную аутентификацию', `<div class="form"><p>Введите текущий TOTP-код или один recovery code. Остальные активные сеансы будут завершены.</p><label>Код<input id="mfa-disable-code" autocomplete="one-time-code" placeholder="123456 или recovery code"></label></div>`, [
      {label:'Отмена',action:closeDialog},
      {label:'Отключить 2FA',danger:true,action:async()=>{const code=$('#mfa-disable-code').value.trim();if(!code){toast('Введите код');return;}try{await api('/auth/mfa/disable',{method:'POST',body:JSON.stringify({code})});trackProduct('mfa.disabled');closeDialog();toast('Двухфакторная аутентификация отключена');await renderSettings();}catch(error){toast(error.message);}}},
    ]);
  });
  $('[data-settings-ai-key]', root)?.addEventListener('click', () => openAIKeySettings().catch(error=>toast(error.message)));
  $('[data-settings-autopilot]', root)?.addEventListener('click', () => openAutopilotSettings().catch(error=>toast(error.message)));
  $('[data-settings-group]', root)?.addEventListener('click', () => openGroupSwitcher());
  $('[data-settings-registrations]', root)?.addEventListener('click', () => openMyEventRegistrations().catch((error)=>toast(error.message)));
  $('[data-settings-notifications]', root)?.addEventListener('click', () => setView('notifications'));
  $('[data-settings-telegram]', root)?.addEventListener('click', () => openTelegramBot().catch((error) => toast(error.message)));
  $('[data-settings-privacy]', root)?.addEventListener('click', () => window.open('/privacy','_blank','noopener'));
  $('[data-settings-consent]', root)?.addEventListener('click', () => window.open('/consent','_blank','noopener'));
  $('[data-settings-logout]', root)?.addEventListener('click', () => $('#logout').click());
  sync();
}

async function openAIKeySettings(){
  const status=await api(aiGroupAwarePath('/ai/keys/mistral'));
  const effective=status.effective_source==='byok'?'Личный ключ':status.effective_source==='server'?'Серверный ключ из админки':status.effective_source==='server_env'?'Серверный ключ из .env':'Активный ключ не найден';
  const byokAllowed=status.byok_allowed!==false&&status.access_enabled!==false;
  const policyNote=!status.access_enabled?'kUni AI отключён администратором для вашего контура. Ключ можно хранить, но запросы не будут выполняться.':status.byok_allowed===false?'BYOK запрещён текущей политикой доступа. Сохранённый ключ останется в профиле и снова заработает, если администратор разрешит BYOK.':status.server_allowed===false?'Системный ключ запрещён политикой; при включённом BYOK запросы будут выполняться только с вашего ключа.':(status.policy_message||'BYOK имеет приоритет над системным ключом, когда разрешён политикой.');
  openDialog('kUni AI · Bring your own key',`<form class="form ai-key-form" id="ai-key-form"><div class="ai-key-summary"><span>✦</span><div><strong>${escapeHTML(effective)}</strong><small>${escapeHTML(policyNote)}</small></div></div><label>Новый Mistral API key <span class="optional">оставь пустым, если не меняешь</span><input name="api_key" type="password" autocomplete="off" placeholder="Вставь ключ Mistral"></label><label class="ai-telegram-option ${byokAllowed?'':'policy-disabled'}"><input type="checkbox" name="enabled" ${status.byok_enabled?'checked':''} ${byokAllowed?'':'disabled'}><span><strong>Использовать мой ключ</strong><small>${byokAllowed?'Когда включено, BYOK имеет приоритет над серверным ключом только для твоих AI-запросов.':'Включение BYOK заблокировано административной политикой.'}</small></span></label>${status.byok_configured?`<div class="ai-key-current"><span>Сохранён</span><strong>${escapeHTML(status.byok_hint||'••••')}</strong></div>`:''}<p class="muted">Ключ проверяется через Mistral перед сохранением и хранится на сервере в зашифрованном виде. Политика доступа не удаляет сохранённый secret.</p></form>`,[
    {label:'Отмена',action:closeDialog},
    ...(status.byok_configured?[{label:'Удалить ключ',danger:true,action:async()=>{await api(aiGroupAwarePath('/ai/keys/mistral'),{method:'DELETE'});toast('Личный AI key удалён');closeDialog();await renderSettings();}}]:[]),
    {label:'Сохранить',primary:true,action:async()=>{const f=$('#ai-key-form');const key=f.api_key.value.trim();const enabled=byokAllowed?Boolean(f.enabled.checked):Boolean(status.byok_enabled);if(key){await api(aiGroupAwarePath('/ai/keys/mistral'),{method:'PUT',body:JSON.stringify({api_key:key,enabled})});}else if(status.byok_configured){await api(aiGroupAwarePath('/ai/keys/mistral'),{method:'PATCH',body:JSON.stringify({enabled})});}else{toast('Вставь API key');return;}toast(byokAllowed?'Настройки kUni AI сохранены':'Ключ сохранён; использование BYOK сейчас запрещено политикой');closeDialog();await renderSettings();}}
  ]);
}

export async function renderSimpleMoreHub() {
  resetHeadActions();
  const adminCard = isAdmin() ? simpleHubCard('admin','Администрирование','Пользователи, структура и журнал','⚙') : '';
  $('#content').innerHTML = `<div class="simple-hub-intro"><span>Остальное</span><h3>Все дополнительные разделы</h3><p>Навигатор здесь остаётся полезным инструментом, но не управляет всей логикой приложения.</p></div><div class="simple-hub-grid">
    ${simpleHubCard('teachers','Преподы','Файлы преподавателей и опыт студентов','👤')}
    ${simpleHubCard('wiki','Wiki','Инструкции и ответы студентов','W')}
    ${simpleHubCard('events','Мероприятия','Хакатоны, встречи и конференции','☆')}
    ${simpleHubCard('council','Студсовет','Обращения и ответы вашего ВУЗа','✉')}
    ${simpleHubCard('notifications','Уведомления','Изменения и напоминания','•')}
    ${simpleHubCard('group','Моя группа','Участники и приглашения','@')}
    ${simpleHubCard('navigator','Навигатор','Корпуса, аудитории и места','⌖')}
    ${adminCard}
  </div>`;
  mountSimpleViewLinks($('#content'));
}

function reportDateInputValue(date) {
  const pad=(value)=>String(value).padStart(2,'0');
  return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}`;
}

function nextWeekReportRange() {
  const now=new Date();
  const mondayOffset=(now.getDay()+6)%7;
  const monday=new Date(now.getFullYear(),now.getMonth(),now.getDate()-mondayOffset+7,12,0,0,0);
  const sunday=new Date(monday); sunday.setDate(sunday.getDate()+6);
  return {from:reportDateInputValue(monday),to:reportDateInputValue(sunday)};
}

function reportDateList(from,to) {
  const out=[]; let cursor=new Date(`${from}T12:00:00Z`); const end=new Date(`${to}T12:00:00Z`);
  while(cursor<=end&&out.length<32){out.push(cursor.toISOString().slice(0,10));cursor.setUTCDate(cursor.getUTCDate()+1);}
  return out;
}

function reportDateLabel(date) {
  return new Intl.DateTimeFormat('ru-RU',{weekday:'short',day:'2-digit',month:'2-digit',timeZone:'UTC'}).format(new Date(`${date}T12:00:00Z`));
}

function reportDateKey(value,timeZone) {
  const parts=new Intl.DateTimeFormat('en-US',{timeZone,year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(new Date(value));
  const map=Object.fromEntries(parts.map((part)=>[part.type,part.value]));
  return `${map.year}-${map.month}-${map.day}`;
}

function reportTime(value,timeZone) {
  return new Intl.DateTimeFormat('ru-RU',{timeZone,hour:'2-digit',minute:'2-digit',hour12:false}).format(new Date(value));
}

function reportPluralPairs(count) {
  const n=Math.abs(Number(count)||0)%100; const n1=n%10;
  if(n>10&&n<20)return 'пар'; if(n1===1)return 'пара'; if(n1>1&&n1<5)return 'пары'; return 'пар';
}

function openReportPopup(title) {
  const popup=window.open('','_blank');
  if(!popup){toast('Браузер заблокировал окно отчёта. Разрешите всплывающие окна для kUni.');return null;}
  popup.document.open();
  popup.document.write(`<!doctype html><meta charset="utf-8"><title>${escapeHTML(title)}</title><body style="font-family:system-ui;padding:32px"><h2>${escapeHTML(title)}</h2><p>Формируем отчёт…</p></body>`);
  popup.document.close();
  return popup;
}

function writePrintableReport(popup,{title,subtitle,body,landscape=true}) {
  if(!popup)return;
  popup.document.open();
  popup.document.write(`<!doctype html><html lang="ru"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${escapeHTML(title)}</title><style>
    @page{size:A4 ${landscape?'landscape':'portrait'};margin:8mm}
    *{box-sizing:border-box}body{margin:0;padding:18px 20px 40px;font-family:Arial,"Segoe UI",sans-serif;color:#15171a;background:#fff;font-size:10.5px;line-height:1.35}
    .toolbar{position:sticky;top:0;z-index:10;display:flex;justify-content:space-between;align-items:center;gap:12px;margin:-18px -20px 18px;padding:10px 20px;background:#fff;border-bottom:1px solid #ddd}.toolbar button{border:0;border-radius:8px;background:#111;color:#fff;padding:9px 14px;font-weight:700;cursor:pointer}
    h1{font-size:22px;line-height:1.1;margin:0 0 5px}h2{font-size:15px;margin:18px 0 7px}h3{font-size:12px;margin:14px 0 6px}.meta{color:#666;margin-bottom:14px}.muted{color:#777}.empty{color:#888;font-style:italic}
    table{width:100%;border-collapse:collapse;table-layout:fixed;margin:8px 0 14px}thead{display:table-header-group}th,td{border:1px solid #cfd3d8;padding:5px 6px;vertical-align:top;overflow-wrap:anywhere}th{background:#f2f4f6;font-size:9.5px;text-align:left}.summary th:first-child{width:105px}.summary td{font-size:9.5px}.summary td strong{display:block;font-size:10.5px}.summary td small{color:#666}
    .details{break-before:page}.group-block{break-inside:avoid-page;margin-bottom:16px}.details-table th:nth-child(1){width:74px}.details-table th:nth-child(3){width:120px}.details-table th:nth-child(4){width:155px}
    .building{break-before:page}.building:first-of-type{break-before:auto}.availability th:first-child{width:90px}.availability td{font-size:8.7px;padding:4px}.availability .free{font-weight:700;margin-bottom:4px}.availability .busy{color:#555;border-top:1px dashed #ddd;padding-top:3px}.availability .busy div+div{margin-top:2px}.room-meta{font-size:8px;color:#777;font-weight:400}
    @media print{body{padding:0;font-size:9.5px}.toolbar{display:none}.building{break-before:page}.building:first-of-type{break-before:auto}a{color:inherit;text-decoration:none}}
  </style></head><body><div class="toolbar"><div><strong>${escapeHTML(title)}</strong><br><span class="muted">${escapeHTML(subtitle||'')}</span></div><button onclick="window.print()">Печать / сохранить PDF</button></div>${body}</body></html>`);
  popup.document.close();
  popup.focus();
  setTimeout(()=>{try{popup.print();}catch{}},300);
}

function uniqueReportInstitutes() {
  const map=new Map();
  for(const group of state.groups||[]){if(group.institute_id&&!map.has(group.institute_id))map.set(group.institute_id,{id:group.institute_id,name:group.institute_name||'Институт',university_id:group.university_id,university_name:group.university_name||'ВУЗ'});}
  return [...map.values()].sort((a,b)=>a.name.localeCompare(b.name,'ru'));
}

function uniqueReportUniversities() {
  const map=new Map();
  for(const group of state.groups||[]){if(group.university_id&&!map.has(group.university_id))map.set(group.university_id,{id:group.university_id,name:group.university_name||'ВУЗ'});}
  return [...map.values()].sort((a,b)=>a.name.localeCompare(b.name,'ru'));
}

function courseReportHTML(report) {
  const dates=reportDateList(report.date_from,report.date_to); const tz=report.institute.timezone||'UTC';
  const summaryRows=(report.groups||[]).map((group)=>{
    const byDate=new Map();
    for(const entry of group.entries||[]){const key=reportDateKey(entry.starts_at,tz);if(!byDate.has(key))byDate.set(key,[]);byDate.get(key).push(entry);}
    return `<tr><th>${escapeHTML(group.name)}</th>${dates.map((date)=>{const entries=(byDate.get(date)||[]).slice().sort((a,b)=>new Date(a.starts_at)-new Date(b.starts_at));if(!entries.length)return '<td class="empty">—</td>';const first=entries[0],last=entries.reduce((best,item)=>new Date(item.ends_at)>new Date(best.ends_at)?item:best,entries[0]);return `<td><strong>${reportTime(first.starts_at,tz)}–${reportTime(last.ends_at,tz)}</strong><small>${entries.length} ${reportPluralPairs(entries.length)}</small></td>`;}).join('')}</tr>`;
  }).join('');
  const summary=`<h1>${escapeHTML(report.institute.short_name||report.institute.name)} · ${report.course} курс</h1><div class="meta">${escapeHTML(report.institute.university_name)} · ${escapeHTML(report.date_from)} — ${escapeHTML(report.date_to)} · часовой пояс ${escapeHTML(tz)}</div><h2>Когда учатся группы</h2><table class="summary"><thead><tr><th>Группа</th>${dates.map((date)=>`<th>${escapeHTML(reportDateLabel(date))}</th>`).join('')}</tr></thead><tbody>${summaryRows||`<tr><td colspan="${dates.length+1}" class="empty">На этом курсе активных групп не найдено.</td></tr>`}</tbody></table>`;
  const details=(report.groups||[]).map((group)=>{
    const byDate=new Map(); for(const entry of group.entries||[]){const key=reportDateKey(entry.starts_at,tz);if(!byDate.has(key))byDate.set(key,[]);byDate.get(key).push(entry);}
    const rows=dates.flatMap((date)=>(byDate.get(date)||[]).sort((a,b)=>new Date(a.starts_at)-new Date(b.starts_at)).map((entry)=>`<tr><td>${escapeHTML(reportDateLabel(date))}<br><strong>${reportTime(entry.starts_at,tz)}–${reportTime(entry.ends_at,tz)}</strong></td><td>${escapeHTML(entry.subject_name||'')}</td><td>${escapeHTML(entry.room_name||'—')}</td><td>${escapeHTML(entry.teacher_name||'—')}</td></tr>`)).join('');
    return `<section class="group-block"><h2>${escapeHTML(group.name)}</h2>${rows?`<table class="details-table"><thead><tr><th>Время</th><th>Предмет</th><th>Аудитория</th><th>Преподаватель</th></tr></thead><tbody>${rows}</tbody></table>`:'<p class="empty">В выбранный период пар нет.</p>'}</section>`;
  }).join('');
  return `${summary}<section class="details"><h1>Подробное расписание</h1>${details||'<p class="empty">Нет данных.</p>'}</section>`;
}

function roomAvailabilityReportHTML(report) {
  const dates=reportDateList(report.date_from,report.date_to); const tz=report.university.timezone||'UTC'; const byBuilding=new Map();
  for(const room of report.rooms||[]){if(!byBuilding.has(room.building))byBuilding.set(room.building,[]);byBuilding.get(room.building).push(room);}
  const buildings=[...byBuilding.keys()].sort((a,b)=>a.localeCompare(b,'ru',{numeric:true}));
  const sections=buildings.map((building)=>{
    const rooms=byBuilding.get(building)||[];
    const rows=rooms.map((room)=>`<tr><th>${escapeHTML(room.room)}${room.capacity?`<div class="room-meta">до ${room.capacity} чел.</div>`:''}</th>${dates.map((date)=>{const day=(room.days||[]).find((item)=>item.date===date)||{free:[],busy:[]};const free=(day.free||[]).map((slot)=>`${reportTime(slot.starts_at,tz)}–${reportTime(slot.ends_at,tz)}`);const busy=(day.busy||[]).map((slot)=>`<div>${reportTime(slot.starts_at,tz)}–${reportTime(slot.ends_at,tz)} · ${escapeHTML(slot.group_name||'')} · ${escapeHTML(slot.subject_name||'')}</div>`).join('');return `<td><div class="free">${free.length?`Свободно: ${escapeHTML(free.join(', '))}`:'Свободных окон нет'}</div><div class="busy">${busy||'Занятий нет'}</div></td>`;}).join('')}</tr>`).join('');
    return `<section class="building"><h1>Корпус ${escapeHTML(building)}</h1><div class="meta">Аудитории: ${rooms.length} · учитываемый период дня ${escapeHTML(report.day_start)}–${escapeHTML(report.day_end)}</div><table class="availability"><thead><tr><th>Аудитория</th>${dates.map((date)=>`<th>${escapeHTML(reportDateLabel(date))}</th>`).join('')}</tr></thead><tbody>${rows}</tbody></table></section>`;
  }).join('');
  return `<h1>Свободные аудитории</h1><div class="meta">${escapeHTML(report.university.name)} · ${escapeHTML(report.date_from)} — ${escapeHTML(report.date_to)} · ${escapeHTML(report.day_start)}–${escapeHTML(report.day_end)} · часовой пояс ${escapeHTML(tz)}</div>${sections||'<p class="empty">Для выбранных корпусов аудитории не найдены.</p>'}`;
}

function openCourseReportDialog() {
  const current=(state.groups||[]).find((group)=>group.id===state.groupID)||state.groups?.[0]||null; const institutes=uniqueReportInstitutes(); const range=nextWeekReportRange();
  if(!institutes.length){toast('Не удалось определить институты из каталога групп.');return;}
  openDialog('PDF · расписание курса',`<form id="course-report-form" class="form"><label>Институт<select name="institute_id" required>${institutes.map((item)=>`<option value="${item.id}" ${item.id===current?.institute_id?'selected':''}>${escapeHTML(item.name)}</option>`).join('')}</select></label><div class="dialog-grid"><label>Курс<select name="course">${Array.from({length:8},(_,i)=>i+1).map((course)=>`<option value="${course}" ${course===Number(current?.current_course||1)?'selected':''}>${course}</option>`).join('')}</select></label><span></span><label>С даты<input type="date" name="from" value="${range.from}" required></label><label>По дату<input type="date" name="to" value="${range.to}" required></label></div><div class="notice">В PDF сначала попадёт сводка «со скольки до скольки учится каждая группа», затем полное расписание по всем группам выбранного курса.</div></form>`,[
    {label:'Отмена',action:closeDialog},
    {label:'Сформировать PDF',primary:true,action:async()=>{const f=$('#course-report-form');if(!f.reportValidity())return;const data=Object.fromEntries(new FormData(f));const popup=openReportPopup('Расписание курса');if(!popup)return;try{const params=new URLSearchParams(data);const report=await api(`/schedule/reports/course?${params.toString()}`);writePrintableReport(popup,{title:`${report.institute.short_name||report.institute.name} · ${report.course} курс`,subtitle:`${report.date_from} — ${report.date_to}`,body:courseReportHTML(report),landscape:true});closeDialog();}catch(error){try{popup.close();}catch{}toast(error?.message||'Не удалось сформировать отчёт');}}
    }
  ]);
}

function preferredReportBuilding(value) {
  const text=String(value||'').toLowerCase(); return ['1','2','4'].some((n)=>new RegExp(`(^|\\D)${n}(\\D|$)`).test(text));
}

function openRoomAvailabilityDialog() {
  const current=(state.groups||[]).find((group)=>group.id===state.groupID)||state.groups?.[0]||null; const universities=uniqueReportUniversities(); const range=nextWeekReportRange();
  if(!universities.length||!current?.university_id){toast('Не удалось определить ВУЗ.');return;}
  const rooms=(state.catalogs.rooms||[]).filter((room)=>room.status!=='archived');
  const buildingOptions=(universityID)=>[...new Set(rooms.filter((room)=>room.university_id===universityID).map((room)=>room.building).filter(Boolean))].sort((a,b)=>a.localeCompare(b,'ru',{numeric:true}));
  const renderBuildings=(universityID)=>buildingOptions(universityID).map((building)=>`<option value="${escapeHTML(building)}" ${preferredReportBuilding(building)?'selected':''}>${escapeHTML(building)}</option>`).join('');
  openDialog('Свободные аудитории',`<form id="room-report-form" class="form"><label>ВУЗ<select name="university_id" required>${universities.map((item)=>`<option value="${item.id}" ${item.id===current.university_id?'selected':''}>${escapeHTML(item.name)}</option>`).join('')}</select></label><label>Корпуса <span class="optional">Ctrl/Cmd — выбрать несколько; ничего не выбрано = весь ВУЗ</span><select name="building" multiple size="7">${renderBuildings(current.university_id)}</select></label><div class="dialog-grid"><label>С даты<input type="date" name="from" value="${range.from}" required></label><label>По дату<input type="date" name="to" value="${range.to}" required></label><label>С какого времени<input type="time" name="day_start" value="08:00" required></label><label>До какого времени<input type="time" name="day_end" value="22:00" required></label></div><div class="notice">По умолчанию выбраны корпуса 1, 2 и 4, если они есть в каталоге. В отчёте каждая аудитория показывается сразу на всю неделю: свободные окна + чем она занята.</div></form>`,[
    {label:'Отмена',action:closeDialog},
    {label:'Сформировать PDF',primary:true,action:async()=>{const f=$('#room-report-form');if(!f.reportValidity())return;const formData=new FormData(f);const params=new URLSearchParams();for(const key of ['university_id','from','to','day_start','day_end'])params.set(key,String(formData.get(key)||''));for(const building of formData.getAll('building'))params.append('building',String(building));const popup=openReportPopup('Свободные аудитории');if(!popup)return;try{const report=await api(`/schedule/rooms/availability?${params.toString()}`);writePrintableReport(popup,{title:'Свободные аудитории',subtitle:`${report.date_from} — ${report.date_to}`,body:roomAvailabilityReportHTML(report),landscape:true});closeDialog();}catch(error){try{popup.close();}catch{}toast(error?.message||'Не удалось сформировать отчёт');}}
    }
  ]);
  const universitySelect=$('#room-report-form [name="university_id"]'); const buildingSelect=$('#room-report-form [name="building"]');
  universitySelect?.addEventListener('change',()=>{buildingSelect.innerHTML=renderBuildings(universitySelect.value);});
}

function openScheduleReportsDialog() {
  openDialog('Отчёты расписания',`<div class="list"><button class="row clickable" type="button" data-schedule-report="course"><div class="row-main"><strong>Расписание курса → PDF</strong><p>Институт + курс + период. Сводка начала/окончания занятий и полное расписание всех групп.</p></div><div class="row-end">→</div></button><button class="row clickable" type="button" data-schedule-report="rooms"><div class="row-main"><strong>Свободные аудитории → PDF</strong><p>Корпуса + период. Для каждой аудитории — свободные окна и занятые интервалы на всю неделю.</p></div><div class="row-end">→</div></button></div>`,[{label:'Закрыть',action:closeDialog}]);
  $('[data-schedule-report="course"]')?.addEventListener('click',()=>{closeDialog();openCourseReportDialog();});
  $('[data-schedule-report="rooms"]')?.addEventListener('click',()=>{closeDialog();openRoomAvailabilityDialog();});
}

export async function renderSchedule() {
  resetHeadActions();
  const teacherMode = state.isGuest && !!state.publicTeacher?.id;
  if (!teacherMode && !requireGroup()) return;
  const weekStart = mondayFor(state.weekOffset); const weekEnd = new Date(weekStart); weekEnd.setDate(weekEnd.getDate()+7);
  headAction('‹', async () => { state.weekOffset--; await renderView(); }, 'button small');
  headAction('Сегодня', async () => { state.weekOffset=0; await renderView(); }, 'button small');
  headAction('›', async () => { state.weekOffset++; await renderView(); }, 'button small');
  if (!teacherMode && !state.isGuest) headAction('Отчёты', openScheduleReportsDialog, 'button small');
  if (canManageGroup()) {
    headAction('Серии', openSeriesManager, 'button small hide-mobile');
    headAction('Преподаватель недоступен', openTeacherUnavailableDialog, 'button small hide-mobile');
    headAction('Добавить пару', () => openScheduleDialog());
  }
  const schedulePath = teacherMode
    ? `/public/teachers/${state.publicTeacher.id}/schedule?from=${encodeURIComponent(weekStart.toISOString())}&to=${encodeURIComponent(weekEnd.toISOString())}`
    : `${state.isGuest ? '/public' : ''}/groups/${state.groupID}/schedule?from=${encodeURIComponent(weekStart.toISOString())}&to=${encodeURIComponent(weekEnd.toISOString())}`;
  const [entries, attendanceResult] = await Promise.all([
    api(schedulePath),
    (state.isGuest || teacherMode) ? Promise.resolve([]) : api(`/attendance/me?group_id=${state.groupID}&from=${encodeURIComponent(weekStart.toISOString())}&to=${encodeURIComponent(weekEnd.toISOString())}`).catch(() => []),
  ]);
  const attendanceItems = Array.isArray(attendanceResult) ? attendanceResult : (attendanceResult?.items || []);
  const attendanceByEntry = new Map(attendanceItems.map((item) => [item.schedule_entry_id, item]));
  const label = `${fmtDate(weekStart,{day:'2-digit',month:'short'})} — ${fmtDate(new Date(weekEnd.getTime()-1),{day:'2-digit',month:'short'})}`;
  const modeLabel = teacherMode ? `Преподаватель · ${escapeHTML(state.publicTeacher.full_name||state.publicTeacher.short_name||'')}` : `Неделя ${isoWeekNumber(weekStart)}`;
  const weekHeader = `<div class="week-overview"><div><span>${modeLabel}</span><strong>${escapeHTML(label)}</strong><small class="schedule-swipe-hint">Свайпните, чтобы сменить неделю</small></div><div class="week-count">${entries.length} ${entries.length===1?'пара':entries.length>1&&entries.length<5?'пары':'пар'}</div></div>`;
  if (!entries.length) {
    $('#content').innerHTML = `${weekHeader}<div class="empty"><strong>На этой неделе пар нет</strong>${canManageGroup() ? 'Добавьте разовую пару или серию на семестр.' : 'Расписание появится после публикации старостой.'}</div>`;
    mountScheduleSwipe();
    return;
  }
  const byDay = entries.reduce((acc,item)=>((acc[new Date(item.starts_at).toDateString()] ||= []).push(item),acc),{});
  $('#content').innerHTML = weekHeader + Object.values(byDay).map((day)=>`
    <div class="section-label day-label ${isToday(day[0].starts_at)?'today':''}">${escapeHTML(fmtDay(day[0].starts_at))}${isToday(day[0].starts_at)?'<span>Сегодня</span>':''}</div><div class="list">${day.map((item)=>`
      <article class="row ${state.isGuest ? '' : 'clickable schedule-row'} ${item.id===state.scheduleFocusEntryID?'schedule-row-focused':''}" data-entry-id="${item.id}">
        <div class="row-time">${fmtTime(item.starts_at)}<br><span class="muted">${fmtTime(item.ends_at)}</span></div>
        <div class="row-main"><strong>${escapeHTML(item.subject_name)}</strong><p>${escapeHTML([teacherMode?item.group_name:item.teacher_name,item.room_name,lessonType(item.lesson_type)].filter(Boolean).join(' · '))}${item.subgroup_id ? ' · подгруппа' : ''}${item.is_exception ? ' · изменено для этой даты' : ''}${item.notes ? `<br>${escapeHTML(item.notes)}` : ''}</p></div>
        <div class="row-end">${attendanceByEntry.has(item.id)?`<span class="pill ${attendanceClass(attendanceByEntry.get(item.id).status)}">${escapeHTML(attendanceLabel(attendanceByEntry.get(item.id).status))}</span>`:''}${item.status==='cancelled'?'<span class="pill danger">Отменено</span>':item.series_id?'<span class="pill">серия</span>':`<span class="pill">r${item.revision}</span>`}</div>
      </article>`).join('')}</div>`).join('');
  $$('.schedule-row').forEach((row)=>row.addEventListener('click',()=>showSchedule(row.dataset.entryId)));
  if (state.scheduleFocusEntryID && entries.some((entry) => entry.id === state.scheduleFocusEntryID)) state.scheduleFocusEntryID = '';
  mountScheduleSwipe();
}

var scheduleSwipeAbort = null;

var windowSwipeAbort = null;

var suppressClickUntil = 0;

function isSwipeFormControl(target) {
  return Boolean(target?.closest?.('input,textarea,select,[contenteditable="true"],.campus-map-stage,.navigator-map,.dialog,.sg-dock,.kuni-quick'));
}

function isSwipeNavControl(target) {
  return Boolean(target?.closest?.('[data-x10-view],[data-open-simple-view],[data-settings-registrations],[data-settings-group],.settings-link,.simple-hub-card'));
}

function animateSwipeSurface(surface, direction) {
  if (!surface) return Promise.resolve();
  surface.style.transition = 'transform .14s ease, opacity .14s ease';
  surface.style.transform = `translate3d(${direction < 0 ? '-24%' : '24%'},0,0)`;
  surface.style.opacity = '.18';
  return new Promise((resolve) => setTimeout(resolve, 145));
}

export function mountScheduleSwipe(surface = null) {
  scheduleSwipeAbort?.abort();
  scheduleSwipeAbort = new AbortController();
  const signal = scheduleSwipeAbort.signal;
  const content = $('#content');
  if (!content || !matchMedia('(max-width:760px)').matches) return;
  if (!surface) {
    surface = document.createElement('div');
    surface.className = 'schedule-swipe-surface';
    while (content.firstChild) surface.append(content.firstChild);
    content.append(surface);
  } else {
    surface.classList.add('schedule-swipe-surface');
  }
  let startX = 0; let startY = 0; let currentX = 0; let startedAt = 0; let horizontal = false; let busy = false;
  const reset = () => {
    surface.style.transition = 'transform .18s ease, opacity .18s ease';
    surface.style.transform = '';
    surface.style.opacity = '';
    setTimeout(() => { surface.style.transition = ''; }, 190);
  };
  surface.addEventListener('touchstart', (event) => {
    if (state.view !== 'schedule' || busy || event.touches.length !== 1 || isSwipeFormControl(event.target)) return;
    startX = currentX = event.touches[0].clientX;
    startY = event.touches[0].clientY;
    startedAt = performance.now();
    horizontal = false;
    if (startX <= 24 || startX >= window.innerWidth - 24) startedAt = 0; // edge swipe = app window
  }, { passive: true, signal });
  surface.addEventListener('touchmove', (event) => {
    if (!startedAt || event.touches.length !== 1 || busy) return;
    currentX = event.touches[0].clientX;
    const dx = currentX - startX; const dy = event.touches[0].clientY - startY;
    if (!horizontal && Math.abs(dx) > 9) horizontal = Math.abs(dx) > Math.abs(dy) * 1.12;
    if (!horizontal) return;
    event.preventDefault();
    suppressClickUntil = performance.now() + 350;
    const resisted = Math.max(-120, Math.min(120, dx * .52));
    surface.style.transform = `translate3d(${resisted}px,0,0)`;
  }, { passive: false, signal });
  surface.addEventListener('touchend', async (event) => {
    if (!startedAt || busy) return;
    const dx = (event.changedTouches[0]?.clientX ?? currentX) - startX;
    const velocity = Math.abs(dx) / Math.max(1, performance.now() - startedAt);
    const shouldChange = horizontal && (Math.abs(dx) >= Math.min(72, window.innerWidth * .17) || (Math.abs(dx) > 32 && velocity > .40));
    startedAt = 0;
    if (!shouldChange) { reset(); return; }
    busy = true;
    suppressClickUntil = performance.now() + 500;
    await animateSwipeSurface(surface, dx < 0 ? -1 : 1);
    state.weekOffset += dx < 0 ? 1 : -1;
    await renderView();
  }, { passive: true, signal });
  surface.addEventListener('touchcancel', () => { startedAt = 0; reset(); }, { passive: true, signal });
}

function primarySwipeWindows() {
  if (state.isGuest) return ['schedule','navigator'];
  if (compactMode()) return ['home','schedule','studyhub','morehub','settings'];
  const orderedSections = normalizeNavigationOrder(state.interfacePreferences.navigation_order);
  const views = orderedSections.map((sectionID) => availableSectionViews(sectionID)[0]?.[0]).filter(Boolean);
  return [...views, 'settings'];
}

function primaryWindowForView(view) {
  if (state.isGuest) return view === 'navigator' ? 'navigator' : 'schedule';
  if (view === 'settings') return 'settings';
  if (compactMode()) return simpleParentView(view);
  const sectionID = sectionForView(view);
  return availableSectionViews(sectionID)[0]?.[0] || view;
}

function submenuParentForSwipe(view) {
  if (state.isGuest) return '';
  if (view === 'home' || view === 'schedule' || view === 'studyhub' || view === 'morehub' || view === 'settings') return '';
  if (!compactMode()) return '';
  if (view === 'lesson') return 'schedule';
  if (view === 'course' || SIMPLE_STUDY_VIEWS.some(([id]) => id === view)) return 'studyhub';
  if (SIMPLE_MORE_VIEWS.some(([id]) => id === view)) return 'morehub';
  const parent = simpleParentView(view);
  return parent && parent !== view ? parent : '';
}

async function switchWindowBySwipe(direction) {
  const parent = submenuParentForSwipe(state.view);
  if (parent) {
    await setView(parent);
    return true;
  }
  const windows = primarySwipeWindows();
  const current = primaryWindowForView(state.view);
  const index = windows.indexOf(current);
  if (index < 0) return false;
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= windows.length) return false;
  await setView(windows[nextIndex]);
  return true;
}

export function mountWindowSwipe() {
  windowSwipeAbort?.abort();
  windowSwipeAbort = new AbortController();
  const signal = windowSwipeAbort.signal;
  const content = $('#content');
  if (!content) return;
  if (!compactMode() && !matchMedia('(max-width:760px)').matches) return;
  /* Bind to #content, not .workspace: a non-passive touchmove on the parent
     cancels Android native pan of both the document and overflow:auto children. */
  const surface = content;
  const workspace = $('.workspace');
  const inSubmenu = () => Boolean(submenuParentForSwipe(state.view));
  content.classList.toggle('has-submenu-swipe', inSubmenu());
  workspace?.classList.toggle('has-submenu-swipe', inSubmenu());
  let startX = 0; let startY = 0; let currentX = 0; let startedAt = 0; let horizontal = false; let busy = false; let pointerId = null;
  const reset = () => {
    content.style.transition = 'transform .18s ease, opacity .18s ease';
    content.style.transform = '';
    content.style.opacity = '';
    setTimeout(() => { content.style.transition = ''; }, 190);
  };
  const begin = (x, y, target) => {
    if (busy || isSwipeFormControl(target) || $('#dialog')?.open) return false;
    // Taps on hub/settings rows must stay clicks — swipe still works from empty surface.
    if (isSwipeNavControl(target)) return false;
    // Schedule keeps center swipe for weeks; submenus use the whole surface.
    if (!inSubmenu() && state.view === 'schedule' && x > 24 && x < window.innerWidth - 24) return false;
    startX = currentX = x;
    startY = y;
    startedAt = performance.now();
    horizontal = false;
    return true;
  };
  const move = (x, y, event) => {
    if (!startedAt || busy) return;
    currentX = x;
    const dx = currentX - startX; const dy = y - startY;
    if (!horizontal && Math.abs(dx) > 16) horizontal = Math.abs(dx) > Math.abs(dy) * 1.25;
    if (!horizontal) return;
    if (pointerId != null) {
      try { surface.setPointerCapture(pointerId); } catch { /* ignore */ }
    }
    if (event?.cancelable) event.preventDefault();
    suppressClickUntil = performance.now() + 350;
    const resisted = Math.max(-92, Math.min(92, dx * .42));
    content.style.transform = `translate3d(${resisted}px,0,0)`;
  };
  const finish = async (x) => {
    if (!startedAt || busy) return;
    const dx = x - startX;
    const velocity = Math.abs(dx) / Math.max(1, performance.now() - startedAt);
    const threshold = inSubmenu() ? Math.min(56, window.innerWidth * .14) : Math.min(82, window.innerWidth * .20);
    const shouldChange = horizontal && (Math.abs(dx) >= threshold || (Math.abs(dx) > 32 && velocity > .40));
    startedAt = 0;
    pointerId = null;
    if (!shouldChange) { reset(); return; }
    busy = true;
    suppressClickUntil = performance.now() + 500;
    const direction = dx < 0 ? 1 : -1;
    const parent = submenuParentForSwipe(state.view);
    const windows = primarySwipeWindows();
    const current = primaryWindowForView(state.view);
    const currentIndex = windows.indexOf(current);
    if (!parent && (currentIndex < 0 || currentIndex + direction < 0 || currentIndex + direction >= windows.length)) {
      busy = false;
      reset();
      return;
    }
    await animateSwipeSurface(content, dx < 0 ? -1 : 1);
    await switchWindowBySwipe(direction);
  };
  surface.addEventListener('touchstart', (event) => {
    if (event.touches.length !== 1) return;
    begin(event.touches[0].clientX, event.touches[0].clientY, event.target);
  }, { passive: true, signal });
  surface.addEventListener('touchmove', (event) => {
    if (event.touches.length !== 1) return;
    move(event.touches[0].clientX, event.touches[0].clientY, event);
  }, { passive: false, signal });
  surface.addEventListener('touchend', (event) => {
    finish(event.changedTouches[0]?.clientX ?? currentX);
  }, { passive: true, signal });
  surface.addEventListener('touchcancel', () => { startedAt = 0; pointerId = null; reset(); }, { passive: true, signal });
  surface.addEventListener('pointerdown', (event) => {
    /* Touch is owned by touch* listeners. Capturing the pointer here
       kills vertical document scroll on Android Chrome / WebView. */
    if (event.pointerType === 'touch') return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.pointerType === 'mouse' && !inSubmenu()) return;
    if (!begin(event.clientX, event.clientY, event.target)) return;
    pointerId = event.pointerId;
  }, { signal });
  surface.addEventListener('pointermove', (event) => {
    if (pointerId == null || event.pointerId !== pointerId) return;
    move(event.clientX, event.clientY, event);
  }, { signal });
  surface.addEventListener('pointerup', (event) => {
    if (pointerId == null || event.pointerId !== pointerId) return;
    finish(event.clientX);
  }, { signal });
  surface.addEventListener('pointercancel', () => { startedAt = 0; pointerId = null; reset(); }, { signal });
}

export function lessonType(value) { return ({ lecture:'лекция', practice:'практика', laboratory:'лабораторная', seminar:'семинар', exam:'экзамен', consultation:'консультация', other:'занятие' })[value] || value; }

export function lessonChoiceCard(entry) {
  if (!entry) return '';
  const meta = [entry.teacher_name, entry.room_name, lessonType(entry.lesson_type)].filter(Boolean).join(' · ');
  return `<div class="lesson-choice-card"><div class="lesson-choice-date"><strong>${fmtDate(entry.starts_at,{day:'2-digit',month:'short'})}</strong><span>${fmtTime(entry.starts_at)}</span></div><div><span>Выбранная пара</span><strong>${escapeHTML(entry.subject_name)}</strong><small>${escapeHTML(meta || 'Без дополнительных данных')}</small></div></div>`;
}

export function lessonAutoSuggestion(entries, now = new Date()) {
  const currentTime = now.getTime();
  const scheduled = (entries || []).filter((entry) => entry?.status === 'scheduled').sort((a,b) => new Date(a.starts_at) - new Date(b.starts_at));
  const ongoing = scheduled.filter((entry) => new Date(entry.starts_at).getTime() <= currentTime && new Date(entry.ends_at).getTime() >= currentTime).sort((a,b) => new Date(b.starts_at) - new Date(a.starts_at))[0];
  const recent = scheduled.filter((entry) => {
    const ended = new Date(entry.ends_at).getTime();
    return ended < currentTime && currentTime - ended <= 6 * 60 * 60 * 1000;
  }).sort((a,b) => new Date(b.ends_at) - new Date(a.ends_at))[0];
  const anchor = ongoing || recent;
  if (!anchor) return null;

  const afterAnchor = scheduled.filter((entry) => entry.id !== anchor.id && new Date(entry.starts_at).getTime() > new Date(anchor.ends_at).getTime());
  let target = null;
  let match = 'series';
  if (anchor.series_id) target = afterAnchor.find((entry) => entry.series_id === anchor.series_id) || null;

  // Old or one-off entries may not have series_id. In that case only accept a
  // conservative 5-16 day recurrence with the same subject/type/subgroup.
  if (!target && !anchor.series_id) {
    match = 'fallback';
    const anchorStart = new Date(anchor.starts_at).getTime();
    const minGap = 5 * 24 * 60 * 60 * 1000;
    const maxGap = 16 * 24 * 60 * 60 * 1000;
    const sameSubgroup = (a,b) => String(a || '') === String(b || '');
    const candidates = afterAnchor.filter((entry) => {
      const gap = new Date(entry.starts_at).getTime() - anchorStart;
      return entry.subject_id === anchor.subject_id &&
        entry.lesson_type === anchor.lesson_type &&
        sameSubgroup(entry.subgroup_id, anchor.subgroup_id) &&
        gap >= minGap && gap <= maxGap;
    });
    target = candidates.find((entry) => anchor.teacher_id && entry.teacher_id === anchor.teacher_id) || candidates[0] || null;
  }
  if (!target) return { anchor, target: null, match };
  const gapDays = Math.round((new Date(target.starts_at).getTime() - new Date(anchor.starts_at).getTime()) / (24 * 60 * 60 * 1000));
  return { anchor, target, match, gapDays };
}

export function initSettingsScheduleRuntime() {
  document.addEventListener('click', (event) => {
    if (performance.now() < suppressClickUntil) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);
}
